import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import {
  getSmsCustomization,
  putSmsCustomization,
  SmsCustomization,
  SmsCustomizationItem
} from '../../../api';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { HorizontalCenterColumnBlock, VerticalHorizontalCenterBlock } from '../../../styled';
import { AgreementNotification, FormFooter, HeaderTitle, Preloader } from '../../ui';
import { LanguageSelector } from './LanguageSelector';
import { initialState, smsPreviewReducer } from './reducer';
import SmsTemplate from './SmsTemplate';
import { PWrapper, SmsContentBlock } from './style';
import { ActionType } from './types';

type Props = RouteComponentProps & {
  projectId: string;
  isAgreementSigned: boolean;
};

const SmsSettingsForm = ({ projectId, isAgreementSigned, history }: Props): JSX.Element => {
  const { navigationPath } = usePaths();
  const intl = useIntl();
  const { showErrorNotify, showSuccessNotify } = useNotifications();
  const [state, dispatch] = useReducer(smsPreviewReducer, initialState);
  const [activeLocale, setActiveLocale] = useState<string[]>([]);

  useEffect(() => {
    if (projectId) {
      getSmsCustomization(projectId)
        .then((data) => {
          dispatch({ type: ActionType.getSmsSuccess, payload: data });
          setActiveLocale(data.map(({ locale }) => locale));
        })
        .catch((err) => {
          showErrorNotify(err);
          dispatch({ type: ActionType.getSmsFail });
        });
    }
  }, [projectId]);

  const backToNavigation = () => history.push({ pathname: navigationPath(projectId) });

  const handleInputChange = useCallback(
    ({ locale, templates }: SmsCustomizationItem) => {
      const payload = state.smsCustomization.map((item) => {
        if (item.locale === locale) {
          return {
            locale,
            templates
          };
        }
        return item;
      });
      dispatch({ type: ActionType.updateSms, payload });
    },
    [state, activeLocale]
  );

  const onSubmit = useCallback(() => {
    dispatch({ type: ActionType.submitStarted });
    const newSmsCustomisation = state.smsCustomization?.filter(
      (item) => activeLocale.indexOf(item.locale) > -1
    );
    putSmsCustomization(projectId, newSmsCustomisation as SmsCustomization)
      .then((data) => {
        showSuccessNotify();
        dispatch({ type: ActionType.submitSuccess, payload: data });
      })
      .catch((err) => {
        showErrorNotify(err);
        dispatch({ type: ActionType.submitFailed });
      });
  }, [state, activeLocale]);

  if (state.fetching) {
    return (
      <VerticalHorizontalCenterBlock>
        <Preloader />
      </VerticalHorizontalCenterBlock>
    );
  }

  return (
    <Form onSubmit={onSubmit}>
      {({ handleSubmit, pristine, values: { fields } }) => {
        return (
          <>
            {!isAgreementSigned && <AgreementNotification />}
            <HeaderTitle
              title={intl.formatMessage({ id: 'sms-customization.settings.title' })}
              subtitle={intl.formatMessage({ id: 'sms-customization.settings.subtitle' })}
              notification={intl.formatMessage(
                { id: 'sms-customization.settings.main.notification' },
                { p: (chunk: string) => <PWrapper>{chunk}</PWrapper> }
              )}
            />
            <HorizontalCenterColumnBlock>
              <SmsContentBlock>
                <LanguageSelector
                  smsData={state.smsCustomization || []}
                  onChange={setActiveLocale}
                />
                {(fields as unknown as string[])?.map((fieldName, index) => {
                  return (
                    <Field key={fieldName} name={fieldName}>
                      {({ input }) => {
                        const smsTexts =
                          state.smsCustomization?.filter(({ locale }) => locale === fieldName)[0]
                            ?.templates || [];
                        return (
                          <SmsTemplate
                            templates={smsTexts}
                            locale={fieldName}
                            openByDefault={fieldName === 'en'}
                            onChange={handleInputChange}
                            projectId={projectId}
                          />
                        );
                      }}
                    </Field>
                  );
                })}
              </SmsContentBlock>
            </HorizontalCenterColumnBlock>

            <FormFooter
              onSubmit={handleSubmit}
              onCancel={backToNavigation}
              fetching={state.submitting}
              disabled={!isAgreementSigned}
              error={state.submitError}
            />
          </>
        );
      }}
    </Form>
  );
};

export default withRouter(SmsSettingsForm);
